import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { currentStep } from "@/app/store";
import { FormProvider, useController, useForm } from "react-hook-form";
import { z } from "zod";
import { Customer, usePanContext } from "@/app/contexts/PanContext";
import { cepService } from "@/app/services/cepService";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import { Button } from "@/view/components/ui/button";
import { states } from "@/app/utils/states";
import { zipCodeMask } from "@/app/utils/zipCodeFormater";
import masks from "@/app/utils/masks";

export const addressFormSchema = z.object({
  zipCode: z
    .string()
    .min(8, "* obrigatório")
    .transform((val) => val.replace(/\D/g, "")),
  neighborhood: z.string().min(4, "* obrigatório"),
  city: z.string().min(4, "* obrigatório"),
  state: z.string({ required_error: "* obrigatório" }).min(2).max(2),
  street: z.string().min(5, "* obrigatório"),
  number: z.string().min(1, "* obrigatório"),
  complement: z.string().optional(),
});

type addressformData = z.infer<typeof addressFormSchema>;

interface AddressFormProps {
  visible?: boolean;
}
export default function AddressForm({ visible }: AddressFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { customer, setCustomer } = usePanContext();

  const { data: isCustomer } = useCustomerCpf(customer?.cpf);

  const addressForm = useForm<addressformData>({
    resolver: zodResolver(addressFormSchema),
  });

  const handleCepSearch = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length !== 8) return;

    try {
      const addressData = await cepService(cleanCep);

      addressForm.setValue("street", addressData.logradouro);
      addressForm.setValue("neighborhood", addressData.bairro);
      addressForm.setValue("city", addressData.localidade);
      addressForm.setValue("state", addressData.uf);
    } catch {
      console.error("CEP não encontrado");
    }
  };

  const {
    field: zipCodeField,
    fieldState: { error: zipCodeError },
  } = useController({
    name: "zipCode",
    control: addressForm.control,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = addressForm;

  useEffect(() => {
    if (isCustomer?.address) {
      addressForm.reset({
        zipCode: masks.zipcode(isCustomer.address.zipCode || ""),
        street: isCustomer.address.street || "",
        number: isCustomer.address.number || "",
        complement: isCustomer.address.complement || "",
        neighborhood: isCustomer.address.neighborhood || "",
        city: isCustomer.address.city || "",
        state: isCustomer.address.state || "",
      });
    }
  }, [isCustomer, addressForm]);

  const submitForm = (data: addressformData) => {
    if (customer) {
      const updatedCustomer: Customer = {
        ...customer,
        address: data,
      };
      setCustomer(updatedCustomer);
      setStep((state) => state + 1);
    }
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <FormProvider {...addressForm}>
        <div className="grid grid-cols-4 gap-4">
          <BasicInput
            name="zipcode"
            label="CEP"
            value={zipCodeField.value || ""}
            error={zipCodeError?.message}
            onChange={(e) => {
              const value = e.target.value;
              zipCodeMask(e);
              zipCodeField.onChange(value);

              const clean = value.replace(/\D/g, "");
              if (clean.length === 8) {
                handleCepSearch(clean);
              }
            }}
          />
        </div>

        <div className="flex gap-4">
          <div className="grow">
            <BasicInput
              name="street"
              label="Endereço"
              error={errors.street?.message}
            />
          </div>
          <div className="w-full max-w-24">
            <BasicInput
              name="number"
              label="Número"
              error={errors.number?.message}
            />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-4 gap-4">
            <BasicInput
              name="complement"
              label="Complemento"
              error={errors.complement?.message}
            />

            <BasicInput
              name="neighborhood"
              label="Bairro"
              error={errors.neighborhood?.message}
            />

            <BasicInput
              name="city"
              label="Cidade"
              error={errors.city?.message}
            />

            <BasicSelect
              label="UF"
              name="state"
              options={states}
              placeholder="Selecione o seu estado"
              error={errors.state?.message}
            />
          </div>
        </div>
      </FormProvider>
      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
